/**
 * @file useTemplateManagement.js
 * @description Template management, protection, and synchronization logic
 *
 * Handles:
 * - Template update detection
 * - Template synchronization
 * - Template status management (active/archived)
 * - Template usage tracking
 * - Integration with useTemplateProtection composable
 */

import { ref, computed } from 'vue';
import { useQuasar } from 'quasar';
import { useTemplateProtection } from '../useTemplateProtection';

export function useTemplateManagement() {
  // ============================================================================
  // STATE
  // ============================================================================

  const $q = useQuasar();

  /**
   * Template details dialog visibility
   * @type {Ref<boolean>}
   */
  const templateDetailsDialogVisible = ref(false);

  /**
   * Current template being viewed/edited
   * @type {Ref<Object|null>}
   */
  const currentTemplate = ref(null);

  /**
   * Template update notifications
   * @type {Ref<Map<number, Object>>}
   */
  const templateUpdateNotifications = ref(new Map());

  /**
   * Loading state for template operations
   * @type {Ref<boolean>}
   */
  const loadingTemplate = ref(false);

  // ============================================================================
  // TEMPLATE PROTECTION INTEGRATION
  // ============================================================================

  const templateProtection = useTemplateProtection();

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Count of entities with available template updates
   */
  const entitiesWithUpdates = computed(() => {
    return templateUpdateNotifications.value.size;
  });

  /**
   * Check if any templates have updates available
   */
  const hasAnyTemplateUpdates = computed(() => {
    return entitiesWithUpdates.value > 0;
  });

  // ============================================================================
  // TEMPLATE UPDATE DETECTION
  // ============================================================================

  /**
   * Check if entity has template update available
   * @param {Object} entity - Equipment or device entity
   * @returns {boolean}
   */
  function hasTemplateUpdate(entity) {
    if (!entity.template_id || !entity.template) {return false;}

    // Check if template was modified after entity
    const templateUpdated = new Date(entity.template.updated_at);
    const entityUpdated = new Date(entity.updated_at);

    return templateUpdated > entityUpdated;
  }

  /**
   * Get template update tooltip text
   * @param {Object} entity - Equipment or device entity
   * @returns {string}
   */
  function getTemplateTooltip(entity) {
    // Check for both equipment templates and controller templates
    const hasEquipmentTemplate = entity.template_id || entity.equipment_template_id;
    const hasControllerTemplate = entity.controller_template_id;

    if (!hasEquipmentTemplate && !hasControllerTemplate) {
      return 'Not using a template';
    }

    // Handle controller templates
    if (hasControllerTemplate) {
      if (!entity.controllerTemplate && !entity.controller_template) {
        return `Using controller template ID: ${entity.controller_template_id}`;
      }
      const controllerTemplate = entity.controllerTemplate || entity.controller_template;
      return `Using controller template: ${controllerTemplate.name}`;
    }

    // Handle equipment templates
    if (!entity.template) {
      return `Using template ID: ${entity.template_id}`;
    }

    if (hasTemplateUpdate(entity)) {
      return `Template "${entity.template.name}" has updates available`;
    }

    return `Using template: ${entity.template.name}`;
  }

  /**
   * Check for template updates across all entities
   * @param {Array} entities - Array of equipment/devices
   */
  function checkForTemplateUpdates(entities) {

    const updates = new Map();

    entities.forEach((entity) => {
      if (hasTemplateUpdate(entity)) {
        updates.set(entity.id, {
          entityId: entity.id,
          entityName: entity.name,
          templateId: entity.template_id,
          templateName: entity.template.name,
          templateUpdatedAt: entity.template.updated_at,
        });
      }
    });

    templateUpdateNotifications.value = updates;

    if (updates.size > 0) {
    }
  }

  /**
   * Clear template update notification for entity
   * @param {number} entityId - Entity ID
   */
  function clearUpdateNotification(entityId) {
    templateUpdateNotifications.value.delete(entityId);
  }

  // ============================================================================
  // TEMPLATE SYNCHRONIZATION
  // ============================================================================

  /**
   * Sync entity with its template
   * @param {Object} entity - Equipment or device
   * @param {Function} apiCallback - Async function to sync via API
   * @returns {Promise<Object>} Updated entity
   */
  async function syncWithTemplate(entity, apiCallback) {
    if (!entity.template_id) {
      $q.notify({
        type: 'warning',
        message: 'Entity is not using a template',
      });
      return null;
    }

    if (!entity.template) {
      $q.notify({
        type: 'warning',
        message: 'Template data not loaded',
      });
      return null;
    }

    try {
      // Check template protection
      const protectionCheck = templateProtection.checkTemplateSync(entity);
      if (!protectionCheck.canSync) {
        $q.notify({
          type: 'warning',
          message: protectionCheck.reason,
        });
        return null;
      }

      const confirmed = await new Promise((resolve) => {
        $q.dialog({
          title: 'Sync with Template',
          message: `Sync "${entity.name}" with template "${entity.template.name}"? This will update the entity to match the template.`,
          cancel: true,
          persistent: false,
        })
          .onOk(() => resolve(true))
          .onCancel(() => resolve(false));
      });

      if (!confirmed) {return null;}

      loadingTemplate.value = true;

      const result = await apiCallback({
        entity_id: entity.id,
        entity_type: entity.type,
        template_id: entity.template_id,
      });

      $q.notify({
        type: 'positive',
        message: 'Synced with template successfully',
      });

      // Clear update notification
      clearUpdateNotification(entity.id);

      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to sync: ${error.message}`,
      });
      throw error;
    } finally {
      loadingTemplate.value = false;
    }
  }

  /**
   * Detach entity from template
   * @param {Object} entity - Equipment or device
   * @param {Function} apiCallback - Async function to detach via API
   * @returns {Promise<Object>} Updated entity
   */
  async function detachFromTemplate(entity, apiCallback) {
    if (!entity.template_id) {
      $q.notify({
        type: 'warning',
        message: 'Entity is not using a template',
      });
      return null;
    }

    if (!entity.template) {
      $q.notify({
        type: 'warning',
        message: 'Template data not loaded',
      });
      return null;
    }

    try {
      // Check template protection
      const protectionCheck = templateProtection.checkTemplateDetach(entity);
      if (!protectionCheck.canDetach) {
        $q.notify({
          type: 'warning',
          message: protectionCheck.reason,
        });
        return null;
      }

      const confirmed = await new Promise((resolve) => {
        $q.dialog({
          title: 'Detach from Template',
          message: `Detach "${entity.name}" from template "${entity.template.name}"? The entity will become independent.`,
          cancel: true,
          persistent: false,
        })
          .onOk(() => resolve(true))
          .onCancel(() => resolve(false));
      });

      if (!confirmed) {return null;}

      loadingTemplate.value = true;

      const result = await apiCallback({
        entity_id: entity.id,
        entity_type: entity.type,
      });

      $q.notify({
        type: 'positive',
        message: 'Detached from template successfully',
      });

      // Clear update notification
      clearUpdateNotification(entity.id);

      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to detach: ${error.message}`,
      });
      throw error;
    } finally {
      loadingTemplate.value = false;
    }
  }

  // ============================================================================
  // TEMPLATE STATUS MANAGEMENT
  // ============================================================================

  /**
   * Toggle template active/archived status
   * @param {Object} template - Template entity
   * @param {Function} apiCallback - Async function to update via API
   * @returns {Promise<Object>} Updated template
   */
  async function toggleTemplateStatus(template, apiCallback) {
    const newStatus = template.is_archived ? 'active' : 'archived';

    try {
      const confirmed = await new Promise((resolve) => {
        $q.dialog({
          title: `${newStatus === 'archived' ? 'Archive' : 'Activate'} Template`,
          message: `${
            newStatus === 'archived' ? 'Archive' : 'Activate'
          } template "${template.name}"?`,
          cancel: true,
          persistent: false,
        })
          .onOk(() => resolve(true))
          .onCancel(() => resolve(false));
      });

      if (!confirmed) {return null;}

      loadingTemplate.value = true;

      const result = await apiCallback({
        template_id: template.id,
        is_archived: newStatus === 'archived',
      });

      $q.notify({
        type: 'positive',
        message: `Template ${newStatus === 'archived' ? 'archived' : 'activated'} successfully`,
      });

      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update template status: ${error.message}`,
      });
      throw error;
    } finally {
      loadingTemplate.value = false;
    }
  }

  // ============================================================================
  // TEMPLATE DETAILS & VIEWING
  // ============================================================================

  /**
   * Show template details dialog
   * @param {Object} template - Template entity
   */
  function showTemplateDetails(template) {

    currentTemplate.value = template;
    templateDetailsDialogVisible.value = true;
  }

  /**
   * Close template details dialog
   */
  function closeTemplateDetails() {
    templateDetailsDialogVisible.value = false;
    currentTemplate.value = null;
  }

  /**
   * View compound template (for compound devices)
   * @param {Object} compoundTemplate - Compound template
   * @param {Function} navigateCallback - Function to navigate to template view
   */
  function viewCompoundTemplate(compoundTemplate, navigateCallback) {

    navigateCallback(compoundTemplate.id);
  }

  /**
   * Edit template from manager
   * @param {Object} template - Template entity
   * @param {Function} editCallback - Function to open template editor
   */
  function editTemplateFromManager(template, editCallback) {

    editCallback(template);
  }

  // ============================================================================
  // TEMPLATE USAGE TRACKING
  // ============================================================================

  /**
   * Get usage count for template
   * @param {Object} template - Template entity
   * @returns {number} Number of entities using this template
   */
  function getTemplateUsageCount(template) {
    return template.usage_count || 0;
  }

  /**
   * Check if template is in use
   * @param {Object} template - Template entity
   * @returns {boolean}
   */
  function isTemplateInUse(template) {
    return getTemplateUsageCount(template) > 0;
  }

  /**
   * Get usage summary for template
   * @param {Object} template - Template entity
   * @returns {string} Human-readable usage summary
   */
  function getTemplateUsageSummary(template) {
    const count = getTemplateUsageCount(template);

    if (count === 0) {
      return 'Not in use';
    }

    const entityType = template.type === 'equipment' ? 'equipment' : 'device';
    return `Used by ${count} ${entityType}${count !== 1 ? 's' : ''}`;
  }

  /**
   * Get template icon based on type and status
   * @param {Object} template - Template entity
   * @returns {string} Icon name
   */
  function getTemplateIcon(template) {
    if (template.is_archived) {
      return 'archive';
    }

    if (template.type === 'compound') {
      return 'account_tree';
    }

    if (template.type === 'equipment') {
      return 'precision_manufacturing';
    }

    return 'memory';
  }

  /**
   * Get template color based on status
   * @param {Object} template - Template entity
   * @returns {string} Color name
   */
  function getTemplateColor(template) {
    if (template.is_archived) {
      return 'grey';
    }

    return 'primary';
  }

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    templateDetailsDialogVisible,
    currentTemplate,
    templateUpdateNotifications,
    loadingTemplate,

    // Computed
    entitiesWithUpdates,
    hasAnyTemplateUpdates,

    // Template protection (re-exported)
    templateProtection,

    // Template update detection
    hasTemplateUpdate,
    getTemplateTooltip,
    checkForTemplateUpdates,
    clearUpdateNotification,

    // Template synchronization
    syncWithTemplate,
    detachFromTemplate,

    // Template status management
    toggleTemplateStatus,

    // Template details & viewing
    showTemplateDetails,
    closeTemplateDetails,
    viewCompoundTemplate,
    editTemplateFromManager,

    // Template usage tracking
    getTemplateUsageCount,
    isTemplateInUse,
    getTemplateUsageSummary,
    getTemplateIcon,
    getTemplateColor,
  };
}
